import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, IconButton, Paper, Tooltip, Typography, Button, Menu, MenuItem, ListItemIcon, TextField, InputAdornment, Divider, FormControlLabel, Switch, useMediaQuery, Checkbox, ListItemText, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Close as CloseIcon, AnalyticsOutlined as AnalyticsIcon, Fullscreen as FullscreenIcon, FullscreenExit as FullscreenExitIcon, Layers as LayersIcon, FilterList as FilterIcon, Edit as EditIcon, CropFree as CropFreeIcon, PanTool as PanToolIcon, Gesture as GestureIcon, ZoomInMap as ZoomInMapIcon, Search as SearchIcon, RestartAlt as RestartAltIcon } from '@mui/icons-material';
import { generateLeaseKey } from '../../utils/leaseKeyUtils';
import { leaseHasSelectedFlags, getWellLeaseIdentity, countyHasSelectedFlags } from '../../utils/flagFilterUtils';
import { COUNTY_COLOR_SCALE, COUNTY_FLAG_COLOR_SCALE } from '../../utils/countyColorScale';
import { calculateLeaseCentroids } from '../../utils/leaseDataProcessor';
import L from 'leaflet';
import EnhancedMap from './EnhancedMap';

const DEFAULT_FLAG_DATASETS = new Set(['wells', 'production', 'inspections', 'violations', 'severances', 'spills']);

const MapViewContainer = ({
  searchResults,
  searchParams,
  activeTabType,
  analyticsPanel,
  analyticsPanelOpen,
  setAnalyticsPanelOpen,
  resultsFilter,
  onClearAnalyticsSelection,
  onClearMapSelectionRef,
  onZoomToDataRef, // Ref to expose zoom function to parent
  leaseFlagsMap,
  countyFlagsMap,
  redFlagsCache,
  onNavigateToLeaseInSummary,
  navigateToLeaseInMapRef,
}) => {
  const [viewMode, setViewMode] = useState('county'); // 'county' | 'lease'
  const [tileLayer, setTileLayer] = useState('default'); // 'default' | 'satellite'
  const [analyticsExpanded, setAnalyticsExpanded] = useState(false); // Track if analytics panel is expanded to full width
  const [analyticsPanelWidth, setAnalyticsPanelWidth] = useState(30); // Panel width as percentage (30% default)
  const [isResizing, setIsResizing] = useState(false);
  const prevAnalyticsPanelOpenRef = useRef(analyticsPanelOpen); // Track previous panel open state
  const [mapFeatureLayer, setMapFeatureLayer] = useState('leases'); // 'wells' | 'leases' - toggle for which layer is visible
  const [wellsLabelsVisible, setWellsLabelsVisible] = useState(false); // Toggle for well name labels
  const [leasesLabelsVisible, setLeasesLabelsVisible] = useState(false); // Toggle for lease name labels
  const [connectionLinesVisible, setConnectionLinesVisible] = useState(false); // Toggle for lease-well links (off by default)
  const [horizBoreholeVisible, setHorizBoreholeVisible] = useState(false); // Toggle for horizontal borehole display (off by default)
  const [layersPanelOpen, setLayersPanelOpen] = useState(false); // Toggle for layers panel visibility
  const [surveyPolygonsVisible, setSurveyPolygonsVisible] = useState(false); // Toggle for survey polygons
  const [surveyPolygonsMinZoom] = useState(11); // Minimum zoom for survey polygons
  const [clustersEnabled, setClustersEnabled] = useState(true); // Toggle for marker clustering
  const [previousClustersEnabled, setPreviousClustersEnabled] = useState(true); // Store user's cluster preference
  const [hideWithoutFlags, setHideWithoutFlags] = useState(false); // Hide counties/leases/wells not tied to selected flags
  const [flagView, setFlagView] = useState(false); // Color/size map by flags instead of records/wells
  const [selectedFlagDatasets, setSelectedFlagDatasets] = useState(() => new Set(DEFAULT_FLAG_DATASETS)); // Selected datasets for flag filtering
  const [flagDatasetMenuOpen, setFlagDatasetMenuOpen] = useState(false); // Dropdown menu state for flag datasets
  const [flagDatasetMenuAnchorEl, setFlagDatasetMenuAnchorEl] = useState(null); // Dropdown anchor for flag datasets
  const [hiddenLeaseDialogOpen, setHiddenLeaseDialogOpen] = useState(false);
  const pendingNavigateRef = useRef(null); // { leaseKey, county } awaiting "Show All & View"
  const [switchToLeaseDialogOpen, setSwitchToLeaseDialogOpen] = useState(false);
  const pendingSelectionModeRef = useRef(null); // selection mode awaiting county→lease confirm
  const [selectionMode, setSelectionMode] = useState(null); // null | 'free' | 'rectangular' | 'freehand' - selection mode type
  const [selectionModeMenuOpen, setSelectionModeMenuOpen] = useState(false); // Dropdown menu state
  const [selectionModeAnchorEl, setSelectionModeAnchorEl] = useState(null); // Dropdown anchor
  const [selectedWells, setSelectedWells] = useState(new Set()); // Selected well IDs (API numbers or indices)
  const [selectedLeases, setSelectedLeases] = useState(new Set()); // Selected lease keys
  const mapInstanceRef = useRef(null); // Reference to the map instance for zoom control
  const openLeasePopupRef = useRef(null); // Ref to expose open popup function from EnhancedMap
  const [mapSearchTerm, setMapSearchTerm] = useState(''); // Search term for filtering map data
  const [mapSearchFocused, setMapSearchFocused] = useState(false); // Track if search field is focused
  const layersIconRef = useRef(null); // Reference to layers icon button for positioning
  const filterIconRef = useRef(null); // Reference to filter/selection icon button for positioning
  const mapShiftAppliedRef = useRef(false); // Track if map has been shifted for current search
  const prevSearchResultsRef = useRef(null); // Track previous search results to detect completion
  const [countiesGeoJSON, setCountiesGeoJSON] = useState(null); // County GeoJSON data from EnhancedMap
  const [countyData, setCountyData] = useState(new Map()); // County data from EnhancedMap

  // Determine if we're on mobile (must be called at top level)
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  // Shared toggle button styling for Map Layers panel
  const toggleButtonGroupSx = {
    '& .MuiToggleButton-root': {
      fontSize: '0.7rem',
      '&.Mui-selected': {
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        '&:hover': {
          backgroundColor: 'primary.dark',
        }
      }
    }
  };

  // Shared Typography styling for section titles in Map Layers panel
  const sectionTitleTypographySx = {
    mb: 1.5,
    display: 'block',
    fontWeight: 600,
    fontSize: '0.75rem',
    variant: 'caption'
  };

  // Shared FormControlLabel styling for switches in Map Layers panel
  const formControlLabelSx = {
    marginLeft: 0,
    '& .MuiFormControlLabel-label': {
      fontSize: '0.75rem',
      marginLeft: '3px' 
    }
  };

  // Shared Switch component props
  const switchProps = {
    color: 'primary',
    size: 'small'
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleTileLayerChange = (event, newLayer) => {
    if (newLayer !== null) {
      setTileLayer(newLayer);
    }
  };

  const toggleAnalyticsPanel = () => {
    if (analyticsExpanded) {
      setAnalyticsExpanded(false);
    }
    setAnalyticsPanelOpen(!analyticsPanelOpen);
  };

  const toggleAnalyticsExpanded = () => {
    setAnalyticsExpanded(!analyticsExpanded);
  };

  // Handle resize drag
  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handleResizeMove = useCallback((e) => {
    if (!isResizing) return;
    
    const containerWidth = window.innerWidth;
    const newWidthPercent = (e.clientX / containerWidth) * 100;
    
    // Constrain between 30% (or 425px equivalent) and 70%
    const minPercent = Math.max(30, (425 / containerWidth) * 100);
    const constrainedWidth = Math.max(minPercent, Math.min(70, newWidthPercent));
    setAnalyticsPanelWidth(constrainedWidth);
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Add mouse event listeners for resize
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Reset expanded state when panel opens programmatically (e.g., from "VIEW IN SUMMARY")
  useEffect(() => {
    // If panel transitions from closed to open, reset expanded state to ensure normal positioning
    if (analyticsPanelOpen && !prevAnalyticsPanelOpenRef.current && analyticsExpanded) {
      setAnalyticsExpanded(false);
    }
    prevAnalyticsPanelOpenRef.current = analyticsPanelOpen;
  }, [analyticsPanelOpen, analyticsExpanded]);

  // Handle selection mode menu
  const handleSelectionModeMenuOpen = (event) => {
    setSelectionModeAnchorEl(event.currentTarget);
    setSelectionModeMenuOpen(true);
  };

  const handleSelectionModeMenuClose = () => {
    setSelectionModeMenuOpen(false);
    setSelectionModeAnchorEl(null);
  };

  const applySelectionMode = (mode) => {
    setSelectionMode(mode);
    setSelectedWells(new Set());
    setSelectedLeases(new Set());
  };

  const handleSelectionModeSelect = (mode) => {
    handleSelectionModeMenuClose();
    // County view has no lease/well markers to select — confirm switch to Location view
    if (viewMode === 'county') {
      pendingSelectionModeRef.current = mode;
      setSwitchToLeaseDialogOpen(true);
      return;
    }
    applySelectionMode(mode);
  };

  const confirmSwitchToLeaseForSelection = () => {
    const mode = pendingSelectionModeRef.current;
    pendingSelectionModeRef.current = null;
    setSwitchToLeaseDialogOpen(false);
    setViewMode('lease');
    setMapFeatureLayer('leases');
    if (mode) {
      applySelectionMode(mode);
    }
  };

  const cancelSwitchToLeaseForSelection = () => {
    pendingSelectionModeRef.current = null;
    setSwitchToLeaseDialogOpen(false);
  };

  const handleDisableSelection = () => {
    // Exit selection mode and clear filter
    setSelectionMode(null);
    setSelectedWells(new Set());
    setSelectedLeases(new Set());
    if (resultsFilter) {
      resultsFilter.clearFilter();
    }
    handleSelectionModeMenuClose();
  };

  const handleCursorMode = () => {
    // Exit selection mode but keep filter active
    setSelectionMode(null);
    setSelectedWells(new Set());
    setSelectedLeases(new Set());
    // Don't clear the filter - keep it active
    handleSelectionModeMenuClose();
  };

  const handleClearFilter = () => {
    // Clear filter but don't change selection mode state
    if (resultsFilter) {
      resultsFilter.clearFilter();
    }
    setSelectedWells(new Set());
    setSelectedLeases(new Set());
    handleSelectionModeMenuClose();
  };

  // Handle box/rectangular selection completion
  const handleBoxSelection = (bounds) => {
    // Use filteredWellsData to only select from visible wells
    if (!filteredWellsData || filteredWellsData.length === 0 || !selectionMode) {
      return;
    }

    if (mapFeatureLayer === 'wells') {
      // Select wells directly
      const newSelectedWells = new Set(selectedWells);
      filteredWellsData.forEach((well, index) => {
        const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
        const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);
        
        if (!isNaN(lat) && !isNaN(lng)) {
          if (lat >= bounds.south && lat <= bounds.north &&
              lng >= bounds.west && lng <= bounds.east) {
            if (hideWithoutFlags) {
              const { leaseKey, county } = getWellLeaseIdentity(well, generateLeaseKey);
              if (!leaseHasSelectedFlags(redFlagsCache, leaseKey, county, selectedFlagDatasets)) {
                return;
              }
            }
            // Use API number as identifier, or index if no API
            const wellId = well['API Number'] || well.api_number || well.api || `well-${index}`;
            newSelectedWells.add(wellId);
          }
        }
      });
      setSelectedWells(newSelectedWells);
    } else if (mapFeatureLayer === 'leases') {
      // Select leases directly
      const newSelectedLeases = new Set(selectedLeases);
      filteredWellsData.forEach(well => {
        const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
        const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);
        
        if (!isNaN(lat) && !isNaN(lng)) {
          if (lat >= bounds.south && lat <= bounds.north &&
              lng >= bounds.west && lng <= bounds.east) {
            const district = well['District'] || well.district || '00';
            const leaseNumber = well['Lease Number'] || well.lease_number || null;
            const leaseName = well['Lease Name'] || well.lease_name || null;
            const county = well['County'] || well.county || null;
            
            const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
            
            // If hideWithoutFlags is enabled, only select leases with flags from selected datasets
            if (hideWithoutFlags) {
              if (!leaseHasSelectedFlags(redFlagsCache, leaseKey, county, selectedFlagDatasets)) {
                return; // Skip leases without flags from selected datasets
              }
            }
            
            newSelectedLeases.add(leaseKey);
          }
        }
      });
      setSelectedLeases(newSelectedLeases);
    }
  };

  // Helper function to check if point is within polygon (ray casting algorithm)
  const isPointInPolygon = (point, polygon) => {
    const latlngs = polygon.getLatLngs()[0]; // Get first ring of polygon
    if (!latlngs || latlngs.length < 3) return false;
    
    let inside = false;
    for (let i = 0, j = latlngs.length - 1; i < latlngs.length; j = i++) {
      const xi = latlngs[i].lng, yi = latlngs[i].lat;
      const xj = latlngs[j].lng, yj = latlngs[j].lat;
      const intersect = ((yi > point.lat) !== (yj > point.lat)) &&
        (point.lng < (xj - xi) * (point.lat - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  // Handle freehand polygon selection completion
  const handleFreehandSelection = (polygon) => {
    // Use filteredWellsData to only select from visible wells
    if (!filteredWellsData || filteredWellsData.length === 0 || !selectionMode || selectionMode !== 'freehand') {
      return;
    }

    if (mapFeatureLayer === 'wells') {
      // Select wells directly within polygon
      const newSelectedWells = new Set(selectedWells);
      filteredWellsData.forEach((well, index) => {
        const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
        const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);
        
        if (!isNaN(lat) && !isNaN(lng)) {
          const point = { lat, lng };
          if (isPointInPolygon(point, polygon)) {
            if (hideWithoutFlags) {
              const { leaseKey, county } = getWellLeaseIdentity(well, generateLeaseKey);
              if (!leaseHasSelectedFlags(redFlagsCache, leaseKey, county, selectedFlagDatasets)) {
                return;
              }
            }
            const wellId = well['API Number'] || well.api_number || well.api || `well-${index}`;
            newSelectedWells.add(wellId);
          }
        }
      });
      setSelectedWells(newSelectedWells);
    } else if (mapFeatureLayer === 'leases') {
      // Select leases directly within polygon
      const newSelectedLeases = new Set(selectedLeases);
      filteredWellsData.forEach(well => {
        const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
        const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);
        
        if (!isNaN(lat) && !isNaN(lng)) {
          const point = { lat, lng };
          if (isPointInPolygon(point, polygon)) {
            const district = well['District'] || well.district || '00';
            const leaseNumber = well['Lease Number'] || well.lease_number || null;
            const leaseName = well['Lease Name'] || well.lease_name || null;
            const county = well['County'] || well.county || null;
            
            const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
            
            if (hideWithoutFlags) {
              if (!leaseHasSelectedFlags(redFlagsCache, leaseKey, county, selectedFlagDatasets)) {
                return;
              }
            }
            
            newSelectedLeases.add(leaseKey);
          }
        }
      });
      setSelectedLeases(newSelectedLeases);
    }
  };

  // Register clear callback with parent
  useEffect(() => {
    if (onClearMapSelectionRef) {
      onClearMapSelectionRef(() => {
        setSelectedWells(new Set());
        setSelectedLeases(new Set());
        setSelectionMode(null);
      });
    }
  }, [onClearMapSelectionRef]);

  // Apply filter from map selection - convert selected wells/leases to lease keys
  const handleApplyMapFilter = () => {
    if (!resultsFilter) return;

    const leaseKeysToFilter = new Set();
    
    // If wells are selected, convert to lease keys
    // Use filteredWellsData to ensure we only process visible wells
    if (selectedWells.size > 0 && filteredWellsData && filteredWellsData.length > 0) {
      filteredWellsData.forEach((well, index) => {
        const wellId = well['API Number'] || well.api_number || well.api || `well-${index}`;
        
        if (selectedWells.has(wellId)) {
          const district = well['District'] || well.district || '00';
          const leaseNumber = well['Lease Number'] || well.lease_number || null;
          const leaseName = well['Lease Name'] || well.lease_name || null;
          const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
          leaseKeysToFilter.add(leaseKey);
        }
      });
    }
    
    // Add directly selected leases
    selectedLeases.forEach(leaseKey => leaseKeysToFilter.add(leaseKey));
    
    if (leaseKeysToFilter.size > 0) {
      resultsFilter.setLeaseKeys(leaseKeysToFilter);
      // Clear the selection after applying filter
      setSelectedWells(new Set());
      setSelectedLeases(new Set());
      // Exit selection mode to close the selection panel
      setSelectionMode(null);
      // Also clear Analytics selection and close its dialog
      if (onClearAnalyticsSelection) {
        onClearAnalyticsSelection();
      }
    }
  };

  // Clear map selection (but keep filter active)
  const handleClearMapSelection = () => {
    setSelectedWells(new Set());
    setSelectedLeases(new Set());
    // Don't clear the filter - just clear the current selection
  };

  // Calculate selection count for display
  const getSelectionCount = () => {
    if (mapFeatureLayer === 'wells') {
      return selectedWells.size;
    } else {
      return selectedLeases.size;
    }
  };

  // Calculate selection counts including counties
  const getSelectionCounts = useMemo(() => {
    if (!searchResults?.data?.wells) {
      return { counties: 0, leases: 0, wells: 0 };
    }

    const countiesSet = new Set();
    let leaseCount = 0;
    let wellCount = 0;

    if (mapFeatureLayer === 'wells' && selectedWells.size > 0) {
      // Count counties and leases from selected wells
      searchResults.data.wells.forEach((well, index) => {
        const wellId = well['API Number'] || well.api_number || well.api || `well-${index}`;
        if (selectedWells.has(wellId)) {
          wellCount++;
          const county = well['County'] || well.county;
          if (county) {
            countiesSet.add(county);
          }
        }
      });
      // Count unique leases from selected wells
      const leasesSet = new Set();
      searchResults.data.wells.forEach((well, index) => {
        const wellId = well['API Number'] || well.api_number || well.api || `well-${index}`;
        if (selectedWells.has(wellId)) {
          const district = well['District'] || well.district || '00';
          const leaseNumber = well['Lease Number'] || well.lease_number || null;
          const leaseName = well['Lease Name'] || well.lease_name || null;
          const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
          leasesSet.add(leaseKey);
        }
      });
      leaseCount = leasesSet.size;
    } else if (mapFeatureLayer === 'leases' && selectedLeases.size > 0) {
      // Count counties from selected leases
      leaseCount = selectedLeases.size;
      searchResults.data.wells.forEach((well) => {
        const district = well['District'] || well.district || '00';
        const leaseNumber = well['Lease Number'] || well.lease_number || null;
        const leaseName = well['Lease Name'] || well.lease_name || null;
        const leaseKey = generateLeaseKey(district, leaseNumber, leaseName);
        
        if (selectedLeases.has(leaseKey)) {
          const county = well['County'] || well.county;
          if (county) {
            countiesSet.add(county);
          }
        }
      });
    }

    return {
      counties: countiesSet.size,
      leases: leaseCount,
      wells: wellCount
    };
  }, [selectedWells, selectedLeases, mapFeatureLayer, searchResults?.data?.wells]);

  // Clear selected leases/wells without matching flags when hide filter is enabled
  useEffect(() => {
    if (!hideWithoutFlags || !redFlagsCache) return;

    if (selectedLeases.size > 0) {
      const filteredLeases = new Set();
      selectedLeases.forEach(leaseKey => {
        if (leaseHasSelectedFlags(redFlagsCache, leaseKey, null, selectedFlagDatasets)) {
          filteredLeases.add(leaseKey);
        }
      });
      if (filteredLeases.size !== selectedLeases.size) {
        setSelectedLeases(filteredLeases);
      }
    }

    if (selectedWells.size > 0 && searchResults?.data?.wells) {
      const filteredWells = new Set();
      searchResults.data.wells.forEach((well, index) => {
        const wellId = well['API Number'] || well.api_number || well.api || `well-${index}`;
        if (!selectedWells.has(wellId)) return;
        const { leaseKey, county } = getWellLeaseIdentity(well, generateLeaseKey);
        if (leaseHasSelectedFlags(redFlagsCache, leaseKey, county, selectedFlagDatasets)) {
          filteredWells.add(wellId);
        }
      });
      if (filteredWells.size !== selectedWells.size) {
        setSelectedWells(filteredWells);
      }
    }
  }, [hideWithoutFlags, redFlagsCache, selectedFlagDatasets, selectedLeases, selectedWells, searchResults?.data?.wells]);

  // Auto-disable clusters when selection mode is active
  useEffect(() => {
    if (selectionMode) {
      // Selection mode active - save current state and disable clusters
      setClustersEnabled(prev => {
        setPreviousClustersEnabled(prev);
        return false;
      });
    } else {
      // No selection mode - restore previous cluster preference
      setClustersEnabled(previousClustersEnabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode]);

  // Function to zoom to visible data (wells/leases/counties) - uses filtered results
  // Optional overrides let Reset Map zoom correctly before state re-renders.
  const handleZoomToData = useCallback((overrides = {}) => {
    if (!mapInstanceRef.current || !searchResults?.data) return;

    const opts = (overrides && typeof overrides === 'object' && !overrides.nativeEvent)
      ? overrides
      : {};
    const mode = opts.viewMode ?? viewMode;
    const featureLayer = opts.mapFeatureLayer ?? mapFeatureLayer;
    const hideFlags = opts.hideWithoutFlags ?? hideWithoutFlags;
    const flagDatasets = opts.selectedFlagDatasets ?? selectedFlagDatasets;

    const map = mapInstanceRef.current;
    const allCoordinates = [];

    if (mode === 'county' && countiesGeoJSON && countyData.size > 0) {
      // County view: zoom to counties that have data (and matching flags when filter is on)
      const countiesWithData = new Set();
      countyData.forEach((data, countyName) => {
        if (data.total > 0) {
          if (
            hideFlags &&
            !countyHasSelectedFlags(redFlagsCache, countyName, flagDatasets)
          ) {
            return;
          }
          countiesWithData.add(countyName.toUpperCase());
        }
      });

      if (countiesWithData.size > 0) {
        // Filter GeoJSON features for counties with data
        const featuresWithData = countiesGeoJSON.features?.filter(feature => {
          const countyName = (feature.properties.NAME || feature.properties.name || '').toUpperCase();
          return countiesWithData.has(countyName);
        });

        if (featuresWithData && featuresWithData.length > 0) {
          // Create a temporary GeoJSON layer to get bounds efficiently
          const filteredGeoJSON = {
            type: 'FeatureCollection',
            features: featuresWithData
          };
          const tempLayer = L.geoJSON(filteredGeoJSON);
          const bounds = tempLayer.getBounds();
          
          if (bounds.isValid()) {
            map.fitBounds(bounds, { padding: [50, 50] });
            return; // Early return since we've handled the zoom
          }
        }
      }
    } else if (featureLayer === 'wells' && searchResults.data.wells) {
      // Collect well coordinates (respect hide-without-flags)
      searchResults.data.wells.forEach(well => {
        if (hideFlags) {
          const { leaseKey, county } = getWellLeaseIdentity(well, generateLeaseKey);
          if (!leaseHasSelectedFlags(redFlagsCache, leaseKey, county, flagDatasets)) {
            return;
          }
        }
        const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
        const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);
        if (!isNaN(lat) && !isNaN(lng)) {
          allCoordinates.push([lat, lng]);
        }
      });
    } else if (featureLayer === 'leases' && mode === 'lease' && searchResults.data.wells) {
      // Collect lease centroids and their well coordinates from filtered results
      const leaseCentroids = calculateLeaseCentroids(searchResults.data.wells);
      leaseCentroids.forEach(lease => {
        if (hideFlags && !leaseHasSelectedFlags(redFlagsCache, lease.leaseKey, lease.county, flagDatasets)) {
          return;
        }
        allCoordinates.push(lease.centroid);
        lease.wells.forEach(well => {
          const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
          const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);
          if (!isNaN(lat) && !isNaN(lng)) {
            allCoordinates.push([lat, lng]);
          }
        });
      });
    }

    if (allCoordinates.length > 0) {
      const bounds = L.latLngBounds(allCoordinates);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [mapFeatureLayer, viewMode, searchResults, countiesGeoJSON, countyData, hideWithoutFlags, redFlagsCache, selectedFlagDatasets]);

  const pendingResetZoomRef = useRef(false);

  const handleResetMap = useCallback(() => {
    pendingResetZoomRef.current = true;
    setViewMode('county');
    setTileLayer('default');
    setMapFeatureLayer('leases');
    setWellsLabelsVisible(false);
    setLeasesLabelsVisible(false);
    setConnectionLinesVisible(false);
    setHorizBoreholeVisible(false);
    setSurveyPolygonsVisible(false);
    setClustersEnabled(true);
    setPreviousClustersEnabled(true);
    setHideWithoutFlags(false);
    setFlagView(false);
    setSelectedFlagDatasets(new Set(DEFAULT_FLAG_DATASETS));
    setSelectionMode(null);
    setSelectedWells(new Set());
    setSelectedLeases(new Set());
    setMapSearchTerm('');
  }, []);

  // After Reset Map, wait until county view has rebuilt countyData (cleared while in Location view), then zoom
  useEffect(() => {
    if (!pendingResetZoomRef.current) return;
    if (viewMode !== 'county') return;
    if (!countiesGeoJSON) return;
    // Still waiting for EnhancedMap to recompute county stats after switching from lease view
    if (searchResults?.data && countyData.size === 0) return;

    pendingResetZoomRef.current = false;
    handleZoomToData({
      viewMode: 'county',
      mapFeatureLayer: 'leases',
      hideWithoutFlags: false,
      selectedFlagDatasets: DEFAULT_FLAG_DATASETS,
    });
  }, [viewMode, countyData, countiesGeoJSON, searchResults, handleZoomToData]);

  // Expose zoom function to parent
  useEffect(() => {
    if (onZoomToDataRef) {
      onZoomToDataRef.current = () => handleZoomToData();
    }
  }, [onZoomToDataRef, handleZoomToData]);

  // Navigate from Summary/report card -> Map (zoom + open lease popup)
  const performNavigateToLease = useCallback((leaseKey, county) => {
    if (isMobile && analyticsPanelOpen) {
      setAnalyticsPanelOpen(false);
    }

    if (viewMode !== 'lease') {
      setViewMode('lease');
    }

    if (mapFeatureLayer !== 'leases') {
      setMapFeatureLayer('leases');
    }

    setTimeout(() => {
      if (searchResults?.data?.wells) {
        const leaseWells = searchResults.data.wells.filter(well => {
          const district = well['District'] || well.district || '00';
          const leaseNumber = well['Lease Number'] || well.lease_number || null;
          const leaseName = well['Lease Name'] || well.lease_name || null;
          const wellLeaseKey = generateLeaseKey(district, leaseNumber, leaseName);
          return wellLeaseKey === leaseKey;
        });

        if (leaseWells.length > 0) {
          const coordinates = [];
          leaseWells.forEach(well => {
            const lat = parseFloat(well['Latitude'] || well.latitude || well.lat);
            const lng = parseFloat(well['Longitude'] || well.longitude || well.lng || well.lon);
            if (!isNaN(lat) && !isNaN(lng)) {
              coordinates.push([lat, lng]);
            }
          });

          if (coordinates.length > 0 && mapInstanceRef.current) {
            const map = mapInstanceRef.current;

            let filteredCoordinates = coordinates;
            if (county) {
              filteredCoordinates = coordinates.filter(([lat, lng]) => {
                const well = leaseWells.find(w => {
                  const wLat = parseFloat(w['Latitude'] || w.latitude || w.lat);
                  const wLng = parseFloat(w['Longitude'] || w.longitude || w.lng || w.lon);
                  return !isNaN(wLat) && !isNaN(wLng) && wLat === lat && wLng === lng;
                });
                if (well) {
                  const wellCounty = (well['County'] || well.county || '').toString().trim();
                  return wellCounty === county;
                }
                return false;
              });
            }

            const openPopupAfterMove = () => {
              setTimeout(() => {
                if (openLeasePopupRef && openLeasePopupRef.current) {
                  openLeasePopupRef.current(leaseKey, county);
                }
              }, 300);
            };

            if (filteredCoordinates.length === 0) {
              filteredCoordinates = coordinates;
            }

            if (filteredCoordinates.length === 1) {
              map.setView(filteredCoordinates[0], 14);
              map.once('moveend', openPopupAfterMove);
            } else {
              const bounds = L.latLngBounds(filteredCoordinates);
              map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
              map.once('moveend', openPopupAfterMove);
            }
          }
        }
      }
    }, 200);
  }, [viewMode, mapFeatureLayer, searchResults, isMobile, analyticsPanelOpen, setAnalyticsPanelOpen]);

  const requestNavigateToLease = useCallback((leaseKey, county) => {
    if (
      hideWithoutFlags &&
      redFlagsCache &&
      !leaseHasSelectedFlags(redFlagsCache, leaseKey, county, selectedFlagDatasets)
    ) {
      pendingNavigateRef.current = { leaseKey, county };
      setHiddenLeaseDialogOpen(true);
      return;
    }
    performNavigateToLease(leaseKey, county);
  }, [hideWithoutFlags, redFlagsCache, selectedFlagDatasets, performNavigateToLease]);

  const confirmShowAllAndViewLease = useCallback(() => {
    const pending = pendingNavigateRef.current;
    pendingNavigateRef.current = null;
    setHiddenLeaseDialogOpen(false);
    setHideWithoutFlags(false);
    if (pending) {
      // Wait for hide filter re-render so the lease marker exists before zoom/popup
      setTimeout(() => {
        performNavigateToLease(pending.leaseKey, pending.county);
      }, 100);
    }
  }, [performNavigateToLease]);

  // Register navigation callback for Summary -> Map navigation
  useEffect(() => {
    if (navigateToLeaseInMapRef) {
      navigateToLeaseInMapRef.current = requestNavigateToLease;
    }
  }, [navigateToLeaseInMapRef, requestNavigateToLease]);

  // Filter wells data based on search term (like FullScreenTable - check all values)
  const filteredWellsData = useMemo(() => {
    if (!mapSearchTerm.trim() || !searchResults?.data?.wells) {
      return searchResults?.data?.wells || [];
    }
    
    const term = mapSearchTerm.toLowerCase();
    return searchResults.data.wells.filter(well => 
      Object.values(well).some(value => 
        value && value.toString().toLowerCase().includes(term)
      )
    );
  }, [mapSearchTerm, searchResults?.data?.wells]);

  // Create filtered searchResults to pass to EnhancedMap
  const filteredSearchResults = useMemo(() => {
    if (!mapSearchTerm.trim()) return searchResults;
    if (!searchResults) return null;
    return {
      ...searchResults,
      data: {
        ...searchResults.data,
        wells: filteredWellsData
      }
    };
  }, [searchResults, filteredWellsData, mapSearchTerm]);

  // Shift map to the right when search completes or page refreshes (desktop only)
  useEffect(() => {
    if (isMobile) return;

    const hasSearchResults = searchResults?.data && Object.keys(searchResults.data).length > 0;
    const prevHasResults = prevSearchResultsRef.current?.data && Object.keys(prevSearchResultsRef.current.data).length > 0;
    const searchJustCompleted = !prevHasResults && hasSearchResults;
    const isPageRefresh = hasSearchResults && !prevSearchResultsRef.current;

    if ((searchJustCompleted || isPageRefresh) && !mapShiftAppliedRef.current) {
      const timer = setTimeout(() => {
        if (!mapInstanceRef.current) return;
        try {
          const map = mapInstanceRef.current;
          const center = map.getCenter();
          const bounds = map.getBounds();
          const lngRange = bounds.getEast() - bounds.getWest();
          const newCenter = L.latLng(center.lat, center.lng + lngRange * -0.2);
          map.panTo(newCenter, { animate: true, duration: 1 });
          mapShiftAppliedRef.current = true;
        } catch (error) {
          // Silently fail
        }
      }, 1500);
      return () => clearTimeout(timer);
    }

    if (!hasSearchResults && prevHasResults) {
      mapShiftAppliedRef.current = false;
    }

    prevSearchResultsRef.current = searchResults;
  }, [searchResults, isMobile]);

  // Reusable Analytics Header Component
  const AnalyticsHeader = ({ onClose, onFullscreen, showFullscreen = true, isFullscreen = false }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: { xs: 2, sm: 3 },
        py: 2,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Typography component="h2" sx={{ fontWeight: 750, fontSize: '1.15rem', letterSpacing: '-0.02em' }}>
        Analytics
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {showFullscreen && (
          <Tooltip title={isFullscreen ? "Exit Full Screen" : "Enter Full Screen"} arrow>
            <IconButton
              onClick={onFullscreen}
              size="small"
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Close Panel" arrow>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  // Map component to avoid duplication
  const mapComponent = (
    <EnhancedMap
          searchResults={filteredSearchResults}
          searchParams={searchParams}
          viewMode={viewMode}
          activeTabType={activeTabType}
          tileLayer={tileLayer}
          mapFeatureLayer={mapFeatureLayer}
          wellsLabelsVisible={wellsLabelsVisible}
          leasesLabelsVisible={leasesLabelsVisible}
          connectionLinesVisible={connectionLinesVisible}
          horizBoreholeVisible={horizBoreholeVisible}
          surveyPolygonsVisible={surveyPolygonsVisible}
          surveyPolygonsMinZoom={surveyPolygonsMinZoom}
          clustersEnabled={clustersEnabled}
          selectionModeEnabled={selectionMode === 'rectangular'}
          onBoxSelection={handleBoxSelection}
          onFreehandSelection={handleFreehandSelection}
          selectedWells={selectedWells}
          selectedLeases={selectedLeases}
          selectionMode={selectionMode}
          onLeaseClick={(leaseKey, lease) => {
            // Toggle lease selection
            if (hideWithoutFlags) {
              const county = lease?.county || null;
              if (!leaseHasSelectedFlags(redFlagsCache, leaseKey, county, selectedFlagDatasets)) {
                return;
              }
            }
            setSelectedLeases(prev => {
              const newSet = new Set(prev);
              if (newSet.has(leaseKey)) {
                newSet.delete(leaseKey);
              } else {
                newSet.add(leaseKey);
              }
              return newSet;
            });
          }}
          onWellClick={(wellId, well) => {
            if (hideWithoutFlags && well) {
              const { leaseKey, county } = getWellLeaseIdentity(well, generateLeaseKey);
              if (!leaseHasSelectedFlags(redFlagsCache, leaseKey, county, selectedFlagDatasets)) {
                return;
              }
            }
            setSelectedWells(prev => {
              const newSet = new Set(prev);
              if (newSet.has(wellId)) {
                newSet.delete(wellId);
              } else {
                newSet.add(wellId);
              }
              return newSet;
            });
          }}
          onMapReady={(map) => {
            mapInstanceRef.current = map;
          }}
          leaseFlagsMap={leaseFlagsMap}
          countyFlagsMap={countyFlagsMap}
          redFlagsCache={redFlagsCache}
          onNavigateToLeaseInSummary={onNavigateToLeaseInSummary}
          navigateToLeaseInMapRef={navigateToLeaseInMapRef}
          openLeasePopupRef={openLeasePopupRef}
          hideWithoutFlags={hideWithoutFlags}
          flagView={flagView}
          selectedFlagDatasets={selectedFlagDatasets}
          onCountiesGeoJSONReady={setCountiesGeoJSON}
          onCountyDataReady={setCountyData}
    />
  );

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', flex: 1 }}>
      {/* Map Container - ALWAYS rendered (never unmounts to preserve zoom/center) */}
      <Box 
        sx={{ 
          width: '100%', 
          height: '100%', 
          position: 'absolute',
          top: 0, 
          left: 0,
          zIndex: 1
        }}
      >
        {mapComponent}
      </Box>

      {/* Map Control Icons - Top Right (below search panel) */}
      {/* Show when analytics is closed on mobile, always show on desktop */}
      {(isMobile ? !analyticsPanelOpen : true) && (
        <Box
        sx={{
          position: 'absolute',
          top: 80, // Position below search panel (16px top + ~64px search panel height)
          right: 16,
          zIndex: 1100,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {/* Map Layers Icon - First */}
        <Tooltip title="Map Layers" arrow placement="left" disableHoverListener={false} enterDelay={200}>
          <IconButton
            ref={layersIconRef}
            onClick={() => setLayersPanelOpen(!layersPanelOpen)}
            sx={{
              backgroundColor: 'background.paper',
              boxShadow: 8,
              width: 40,
              height: 40,
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'background.default',
                boxShadow: 10
              }
            }}
          >
            <LayersIcon />
          </IconButton>
        </Tooltip>

        {/* Selection Mode Icon - Second */}
        <Tooltip title="Selection Mode" arrow placement="left" disableHoverListener={false} enterDelay={200}>
          <IconButton
            ref={(el) => { 
              setSelectionModeAnchorEl(el);
              filterIconRef.current = el;
            }}
            onClick={handleSelectionModeMenuOpen}
            sx={{
              backgroundColor: (selectionMode || resultsFilter?.isFilterActive) ? 'primary.main' : 'background.paper',
              ...((selectionMode || resultsFilter?.isFilterActive) ? { color: 'primary.contrastText' } : { color: 'text.primary' }),
              boxShadow: 8,
              width: 40,
              height: 40,
              '&:hover': {
                backgroundColor: (selectionMode || resultsFilter?.isFilterActive) ? 'primary.dark' : 'background.default',
                boxShadow: 10
              }
            }}
          >
            <FilterIcon />
          </IconButton>
        </Tooltip>

        {/* Auto-Zoom Icon - Last (moved to bottom) */}
        <Tooltip title="Zoom to Data" arrow placement="left" disableHoverListener={false} enterDelay={200}>
          <IconButton
            onClick={() => handleZoomToData()}
            sx={{
              backgroundColor: 'background.paper',
              boxShadow: 8,
              width: 40,
              height: 40,
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'background.default',
                boxShadow: 10
              }
            }}
          >
            <ZoomInMapIcon />
          </IconButton>
        </Tooltip>

        {/* Selection Mode Dropdown Menu */}
        <Menu
          anchorEl={selectionModeAnchorEl}
          open={selectionModeMenuOpen}
          onClose={handleSelectionModeMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              '& .MuiMenuItem-root': {
                paddingLeft: '12px !important',
                paddingRight: '12px !important',
                minHeight: '40px',
                justifyContent: 'center',
              },
              '& .MuiListItemIcon-root': {
                minWidth: 'auto !important',
                marginRight: '0 !important',
                marginLeft: '0 !important',
              }
            }
          }}
        >
          <Tooltip title="Point Selection Mode" arrow placement="right">
            <MenuItem onClick={() => handleSelectionModeSelect('free')}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
            </MenuItem>
          </Tooltip>
          <Tooltip title="Rectangular Selection Mode" arrow placement="right">
            <MenuItem onClick={() => handleSelectionModeSelect('rectangular')}>
              <ListItemIcon>
                <CropFreeIcon fontSize="small" />
              </ListItemIcon>
            </MenuItem>
          </Tooltip>
          <Tooltip title="Freehand Selection Mode" arrow placement="right">
            <MenuItem onClick={() => handleSelectionModeSelect('freehand')}>
              <ListItemIcon>
                <GestureIcon fontSize="small" />
              </ListItemIcon>
            </MenuItem>
          </Tooltip>
          {selectionMode && (
            <>
              <Tooltip title="Cursor Mode" arrow placement="right">
                <MenuItem onClick={handleCursorMode}>
                  <ListItemIcon>
                    <PanToolIcon fontSize="small" />
                  </ListItemIcon>
                </MenuItem>
              </Tooltip>
              <Tooltip title="Disable Selection & Clear Filter" arrow placement="right">
                <MenuItem onClick={handleDisableSelection}>
                  <ListItemIcon>
                    <CloseIcon fontSize="small" />
                  </ListItemIcon>
                </MenuItem>
              </Tooltip>
            </>
          )}
          {!selectionMode && resultsFilter?.isFilterActive && (
            <Tooltip title="Clear Filter" arrow placement="right">
              <MenuItem onClick={handleClearFilter}>
                <ListItemIcon>
                  <CloseIcon fontSize="small" />
                </ListItemIcon>
              </MenuItem>
            </Tooltip>
            )}
          </Menu>
        </Box>
      )}

      {/* Selection Controls Panel - Show when selection mode is active */}
      {selectionMode && filterIconRef.current && (isMobile ? !analyticsPanelOpen : true) && (
        <Box
          sx={{
            position: 'absolute',
            top: filterIconRef.current.offsetTop + filterIconRef.current.offsetHeight + 8,
            right: 16,
            zIndex: 1100,
            minWidth: 200,
          }}
        >
          <Paper
            sx={{
              p: 2,
              boxShadow: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Selection
              </Typography>
              <Tooltip>
                <IconButton
                  size="small"
                  onClick={handleCursorMode}
                  sx={{
                    padding: 0.5,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {getSelectionCount() > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                {getSelectionCounts.counties > 0 && `${getSelectionCounts.counties} Count${getSelectionCounts.counties !== 1 ? 'ies' : 'y'}, `}
                {mapFeatureLayer === 'wells' 
                  ? `${getSelectionCounts.wells} Well${getSelectionCounts.wells !== 1 ? 's' : ''} Selected`
                  : `${getSelectionCounts.leases} Lease${getSelectionCounts.leases !== 1 ? 's' : ''} Selected`
                }
              </Typography>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyMapFilter}
              disabled={getSelectionCount() === 0}
              color="primary"
              fullWidth
            >
              Apply Filter
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearMapSelection}
              disabled={getSelectionCount() === 0}
              color="primary"
              fullWidth
            >
              Clear Selection
            </Button>
          </Paper>
        </Box>
      )}

      {/* Map Well Search Panel - Always Visible Top Right when map is visible */}
      {(isMobile ? !analyticsPanelOpen : true) && (
        <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
            zIndex: 1100,
            minWidth: 250,
          }}
        >
        <Paper
          sx={{
            p: 0,
            boxShadow: 8,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder={mapSearchFocused ? "Enter API, lease name or #" : "Well Search"}
            value={mapSearchTerm}
            onChange={(e) => setMapSearchTerm(e.target.value)}
            onFocus={() => setMapSearchFocused(true)}
            onBlur={() => setMapSearchFocused(false)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                fontSize: '0.875rem'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.primary' }} />
                </InputAdornment>
              ),
              endAdornment: mapSearchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setMapSearchTerm('')}
                    edge="end"
                    sx={{ padding: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          {mapSearchTerm.trim() && (
            <Box sx={{ px: 1.5, pb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Showing {filteredWellsData.length} of {searchResults?.data?.wells?.length || 0} wells
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
      )}

      {/* Layers Panel - Top Right (below layers icon) */}
      {layersPanelOpen && layersIconRef.current && (isMobile ? !analyticsPanelOpen : true) && (
        <Box
          sx={{
            position: 'absolute',
            top: layersIconRef.current.offsetTop + layersIconRef.current.offsetHeight + 8,
            right: 16,
            zIndex: 1100,
            width: 225,
          }}
        >
          <Paper
            sx={{
              p: 1,
              boxShadow: 8,
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'calc(100vh - 200px)',
              overflow: 'auto',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Map Layers
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Tooltip title="Reset Map" arrow>
                  <IconButton
                    size="small"
                    onClick={handleResetMap}
                    sx={{
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={() => setLayersPanelOpen(false)}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', pr: 0.5 }}>

            {/* View Mode Section - Consolidated */}
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={sectionTitleTypographySx}>
                View Mode
              </Typography>
              
              {/* View Mode Toggle */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                aria-label="view mode"
                size="small"
                fullWidth
                sx={{ 
                  mb: 1.5,
                  ...toggleButtonGroupSx
                }}
              >
                <ToggleButton value="county" aria-label="county view">
                  County
                </ToggleButton>
                <ToggleButton value="lease" aria-label="lease view">
                  Location
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Base Map Toggle */}
              <ToggleButtonGroup
                value={tileLayer}
                exclusive
                onChange={handleTileLayerChange}
                aria-label="tile layer"
                size="small"
                fullWidth
                sx={toggleButtonGroupSx}
              >
                <ToggleButton value="default" aria-label="default map">
                  Default
                </ToggleButton>
                <ToggleButton value="satellite" aria-label="satellite map">
                  Satellite
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Hide without Flags — map-level (county + location views) */}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography sx={sectionTitleTypographySx}>
                Flag Filter
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 0 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={flagView}
                      onChange={(e) => setFlagView(e.target.checked)}
                      {...switchProps}
                    />
                  }
                  label="Flag View"
                  sx={formControlLabelSx}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={hideWithoutFlags}
                      onChange={(e) => setHideWithoutFlags(e.target.checked)}
                      {...switchProps}
                    />
                  }
                  label="Hide without Flags"
                  sx={formControlLabelSx}
                />
                {(hideWithoutFlags || flagView) && (
                  <Box sx={{ ml: 0, mt: 0.5 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        setFlagDatasetMenuAnchorEl(e.currentTarget);
                        setFlagDatasetMenuOpen(true);
                      }}
                      sx={{
                        fontSize: '0.7rem',
                        textTransform: 'none',
                        minWidth: 'auto',
                        px: 1,
                        py: 0.5,
                        borderColor: 'divider',
                        color: 'text.secondary',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      Filter by Dataset ({selectedFlagDatasets.size})
                    </Button>
                    <Menu
                      anchorEl={flagDatasetMenuAnchorEl}
                      open={flagDatasetMenuOpen}
                      onClose={() => {
                        setFlagDatasetMenuOpen(false);
                        setFlagDatasetMenuAnchorEl(null);
                      }}
                      PaperProps={{
                        sx: {
                          maxHeight: 300,
                          width: '200px'
                        }
                      }}
                    >
                      {[
                        { key: 'wells', label: 'Wells' },
                        { key: 'production', label: 'Production' },
                        { key: 'inspections', label: 'Inspections' },
                        { key: 'violations', label: 'Violations' },
                        { key: 'severances', label: 'Severances' },
                        { key: 'spills', label: 'Spills' }
                      ].map((dataset) => (
                        <MenuItem
                          key={dataset.key}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFlagDatasets(prev => {
                              const newSet = new Set(prev);
                              if (newSet.has(dataset.key)) {
                                newSet.delete(dataset.key);
                              } else {
                                newSet.add(dataset.key);
                              }
                              if (newSet.size === 0) {
                                newSet.add(dataset.key);
                              }
                              return newSet;
                            });
                          }}
                          dense
                        >
                          <Checkbox
                            checked={selectedFlagDatasets.has(dataset.key)}
                            size="small"
                          />
                          <ListItemText primary={dataset.label} />
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                )}
              </Box>
            </Box>

            {/* County choropleth legend */}
            {viewMode === 'county' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography sx={sectionTitleTypographySx}>
                    {flagView ? 'Flag Share' : 'Record Share'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontSize: '0.65rem' }}>
                    {flagView
                      ? 'County color by share of total flags (selected datasets)'
                      : 'County color by share of total matched records'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, ml: 0.5 }}>
                    {(flagView ? COUNTY_FLAG_COLOR_SCALE : COUNTY_COLOR_SCALE).map(({ label, color }) => (
                      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 14,
                            backgroundColor: color,
                            border: '1px solid #666',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                          {label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {/* Only show well/lease toggles when in lease view */}
            {viewMode === 'lease' && (
              <>
                {/* Map Features Toggle - part of View Mode section */}
                <ToggleButtonGroup
                  value={mapFeatureLayer}
                  exclusive
                  onChange={(e, newLayer) => {
                    if (newLayer !== null) {
                      setMapFeatureLayer(newLayer);
                    }
                  }}
                  aria-label="map feature layer"
                  size="small"
                  fullWidth
                  sx={{ 
                    mb: 1,
                    ...toggleButtonGroupSx
                  }}
                >
                  <ToggleButton value="wells" aria-label="wells">
                    Wells
                  </ToggleButton>
                  <ToggleButton value="leases" aria-label="leases">
                    Leases
                  </ToggleButton>
                </ToggleButtonGroup>

                <Divider sx={{ my: 2 }} />

                {/* Map Features Section */}
                <Box sx={{ mb: 2 }}>
                  <Typography sx={sectionTitleTypographySx}>
                    Map Features
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 0 }}>
                    {/* Clusters Toggle */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={clustersEnabled}
                          onChange={(e) => setClustersEnabled(e.target.checked)}
                          {...switchProps}
                        />
                      }
                      label="Locations Clusters"
                      sx={formControlLabelSx}
                    />
                    
                    {/* Survey Polygons Toggle */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={surveyPolygonsVisible}
                          onChange={(e) => setSurveyPolygonsVisible(e.target.checked)}
                          {...switchProps}
                        />
                      }
                      label="Survey Polygons"
                      sx={formControlLabelSx}
                    />
                    
                    {/* Well Name Labels Toggle - Only show when wells layer is selected */}
                    {mapFeatureLayer === 'wells' && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={wellsLabelsVisible}
                            onChange={(e) => setWellsLabelsVisible(e.target.checked)}
                            {...switchProps}
                          />
                        }
                        label="Well Names"
                        sx={formControlLabelSx}
                      />
                    )}
                    
                    {/* Horiz Borehole Toggle - Only show when wells layer is selected */}
                    {mapFeatureLayer === 'wells' && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={horizBoreholeVisible}
                            onChange={(e) => setHorizBoreholeVisible(e.target.checked)}
                            {...switchProps}
                          />
                        }
                        label="Horizontal Boreholes"
                        sx={formControlLabelSx}
                      />
                    )}
                    
                    {/* Lease Name Labels Toggle - Only show when leases layer is selected */}
                    {mapFeatureLayer === 'leases' && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={leasesLabelsVisible}
                            onChange={(e) => setLeasesLabelsVisible(e.target.checked)}
                            {...switchProps}
                          />
                        }
                        label="Lease Names"
                        sx={formControlLabelSx}
                      />
                    )}
                    
                    {/* Lease-Well Links Toggle - Show for both wells and leases views */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={connectionLinesVisible}
                          onChange={(e) => setConnectionLinesVisible(e.target.checked)}
                          {...switchProps}
                        />
                      }
                      label="Lease-Well Links"
                      sx={formControlLabelSx}
                    />
                  </Box>
                </Box>


                {/* Cluster Legend - Show for both wells and leases, only when clusters are enabled */}
                {viewMode === 'lease' && clustersEnabled && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={sectionTitleTypographySx}>
                        Clusters
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 0.5 }}>
                        {[
                          { label: 'Small (1-9)', color: 'rgba(110, 204, 57, 0.6)' },
                          { label: 'Medium (10-99)', color: 'rgba(240, 194, 12, 0.6)' },
                          { label: 'Large (100+)', color: 'rgba(241, 128, 23, 0.6)' },
                        ].map(({ label, color }) => (
                          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: color,
                                border: '1.5px solid #666',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  color: '#000',
                                  lineHeight: 1,
                                }}
                              >
                                {label.includes('Small') ? '1' : label.includes('Medium') ? '10' : '100'}
                              </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                              {label}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </>
                )}

                {/* Well Legend - Only show if wells layer is selected */}
                {mapFeatureLayer === 'wells' && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={sectionTitleTypographySx}>
                        Wells
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {/* Well Group Shapes */}
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                            Well Group
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 0.5 }}>
                            {[
                              { group: 'ACTIVE', color: '#4caf50', isCircle: true },
                              { group: 'PERMIT', color: '#607d8b', isPentagon: true },
                              { group: 'SHUT IN', color: '#ffc107', isCircle: true, isHalfFill: true },
                              { group: 'TA', color: '#2196f3', isTriangle: true },
                              { group: 'PARTIAL PLUG', color: '#ff9800', isTriangle: true },
                              { group: 'P&A', color: '#8d6e63', isCircle: true, hasDiagonal: true },
                              { group: 'DRY HOLE', color: '#e91e63', isCircle: true, hasCross: true },
                              { group: 'MULTI', color: '#9e9e9e', isHexagon: true },
                            ].map(({ group, color, isCircle, isPentagon, isTriangle, isHalfFill, hasDiagonal, hasCross, isHexagon }) => (
                              <Box key={group} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 14,
                                    height: 14,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    position: 'relative',
                                  }}
                                >
                                  {isCircle && !isHalfFill && !hasDiagonal && !hasCross && (
                                    <Box
                                      sx={{
                                        width: 12,
                                        height: 12,
                                        backgroundColor: color,
                                        border: '2px solid #666',
                                        borderRadius: '50%',
                                        position: 'relative',
                                      }}
                                    />
                                  )}
                                  {isCircle && hasDiagonal && (
                                    <Box
                                      component="svg"
                                      width="14"
                                      height="14"
                                      viewBox="0 0 14 14"
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                      }}
                                    >
                                      <circle
                                        cx="7"
                                        cy="7"
                                        r="5"
                                        fill={color}
                                        stroke="#666"
                                        strokeWidth="1.5"
                                      />
                                      <line
                                        x1="2"
                                        y1="2"
                                        x2="12"
                                        y2="12"
                                        stroke="#666"
                                        strokeWidth="1.5"
                                      />
                                    </Box>
                                  )}
                                  {isCircle && hasCross && (
                                    <Box
                                      component="svg"
                                      width="14"
                                      height="14"
                                      viewBox="0 0 14 14"
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                      }}
                                    >
                                      <circle
                                        cx="7"
                                        cy="7"
                                        r="5"
                                        fill={color}
                                        stroke="#666"
                                        strokeWidth="1.5"
                                      />
                                      <line
                                        x1="2"
                                        y1="2"
                                        x2="12"
                                        y2="12"
                                        stroke="#666"
                                        strokeWidth="1.5"
                                      />
                                      <line
                                        x1="12"
                                        y1="2"
                                        x2="2"
                                        y2="12"
                                        stroke="#666"
                                        strokeWidth="1.5"
                                      />
                                    </Box>
                                  )}
                                  {isCircle && isHalfFill && (
                                    <Box
                                      component="svg"
                                      width="14"
                                      height="14"
                                      viewBox="0 0 14 14"
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                      }}
                                    >
                                      <path
                                        d="M 7 7 L 7 2 A 5 5 0 0 1 7 12 Z"
                                        fill={color}
                                      />
                                      <circle
                                        cx="7"
                                        cy="7"
                                        r="5"
                                        fill="none"
                                        stroke="#666"
                                        strokeWidth="1.5"
                                      />
                                    </Box>
                                  )}
                                  {isPentagon && (
                                    <Box
                                      component="svg"
                                      width="14"
                                      height="14"
                                      viewBox="0 0 14 14"
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                      }}
                                    >
                                      <polygon
                                        points="7,2 11,5 10,10 4,10 3,5"
                                        fill={color}
                                        stroke="#666"
                                        strokeWidth="1.5"
                                      />
                                    </Box>
                                  )}
                                  {isTriangle && (
                                    <Box
                                      component="svg"
                                      width="14"
                                      height="14"
                                      viewBox="0 0 14 14"
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                      }}
                                    >
                                      <path
                                        d="M 7 2 L 7 12 L 12 12 Z"
                                        fill={color}
                                      />
                                      <polygon
                                        points="7,2 12,12 2,12"
                                        fill="none"
                                        stroke="#666"
                                        strokeWidth="1.5"
                                      />
                                    </Box>
                                  )}
                                  {isHexagon && (
                                    <Box
                                      component="svg"
                                      width="14"
                                      height="14"
                                      viewBox="0 0 14 14"
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                      }}
                                    >
                                      <polygon
                                        points="7,2.5 10.897,4.25 10.897,9.75 7,11.5 3.103,9.75 3.103,4.25"
                                        fill="none"
                                        stroke="#666"
                                        strokeWidth="1.5"
                                      />
                                    </Box>
                                  )}
                                </Box>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                  {group}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                        {/* Lease-Well Links Legend Item - Only show when lease-well links are visible */}
                        {connectionLinesVisible && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 0.5 }}>
                            <Box
                              sx={{
                                width: 16,
                                borderTop: '2px dashed #000',
                                flexShrink: 0,
                              }}
                            />
                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                              Lease-well links to lease centroids
                            </Typography>
                          </Box>
                        )}
                        {/* Well Size by Inactive Years */}
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block', fontSize: '0.7rem' }}>
                            Size (by Inactive Years)
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                backgroundColor: '#9e9e9e',
                                border: '1.5px solid #666',
                                borderRadius: '50%',
                              }}
                            />
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>0 yrs</Typography>
                            </Box>
                            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  backgroundColor: '#9e9e9e',
                                  border: '1.5px solid #666',
                                  borderRadius: '50%',
                                }}
                              />
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>10+ yrs</Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}

                {/* Lease Legend - Only show if leases layer is selected */}
                {mapFeatureLayer === 'leases' && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={sectionTitleTypographySx}>
                        Leases
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 0.5 }}>
                        {flagView ? (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  backgroundColor: '#61ce70',
                                  border: '2px solid #666',
                                  borderRadius: '4px',
                                  transform: 'rotate(45deg)',
                                  flexShrink: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Box
                                  sx={{
                                    transform: 'rotate(-45deg)',
                                    color: 'white',
                                    fontSize: '8px',
                                    fontWeight: 600,
                                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                    lineHeight: 1,
                                  }}
                                >
                                  0
                                </Box>
                              </Box>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                No flags
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 16,
                                  height: 16,
                                  backgroundColor: '#f44336',
                                  border: '2px solid #666',
                                  borderRadius: '4px',
                                  transform: 'rotate(45deg)',
                                  flexShrink: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Box
                                  sx={{
                                    transform: 'rotate(-45deg)',
                                    color: 'white',
                                    fontSize: '8px',
                                    fontWeight: 600,
                                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                    lineHeight: 1,
                                  }}
                                >
                                  3
                                </Box>
                              </Box>
                              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                Has flags (red shade and size by flag count)
                              </Typography>
                            </Box>
                          </>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                backgroundColor: '#1c648c',
                                border: '2px solid #666',
                                borderRadius: '4px',
                                transform: 'rotate(45deg)',
                                flexShrink: 0,
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Box
                                sx={{
                                  transform: 'rotate(-45deg)',
                                  color: 'white',
                                  fontSize: '8px',
                                  fontWeight: 600,
                                  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                  lineHeight: 1,
                                }}
                              >
                                3
                              </Box>
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -6,
                                  right: -2,
                                  width: 8,
                                  height: 8,
                                  backgroundColor: '#f44336',
                                  borderRadius: '50%',
                                  border: '1px solid white',
                                }}
                              />
                            </Box>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                              Lease centroids (number = well count; blue shade by well count; red dot = has flags)
                            </Typography>
                          </Box>
                        )}
                        {/* Lease-Well Links Legend Item - Only show when lease-well links are visible */}
                        {connectionLinesVisible && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                borderTop: '2px dashed #000',
                                flexShrink: 0,
                              }}
                            />
                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                              Lease-well links to associated wells
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </>
                )}
              </>
            )}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Analytics Panel Toggle Button - Only show when panel is collapsed */}
      {!analyticsPanelOpen && (
        <Tooltip title="Expand Analytics Panel" arrow placement="right">
          <IconButton
            onClick={toggleAnalyticsPanel}
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              zIndex: 1200,
              backgroundColor: 'background.paper',
              boxShadow: 8,
              width: 40,
              height: 40,
              borderRadius: '50%',
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'background.default',
                boxShadow: 10
              }
            }}
          >
            <AnalyticsIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Analytics Overlay - Positioned to look like split screen */}
      {/* Always render analyticsPanel to keep components mounted for popups, but hide when closed */}
        <Box
          sx={{
          position: analyticsPanelOpen ? (isMobile ? 'fixed' : 'absolute') : 'fixed',
            top: isMobile ? { xs: '56px', sm: '64px' } : 0, // Account for AppBar on mobile
            left: 0,
            right: analyticsExpanded ? 0 : (isMobile ? 0 : 'auto'),
            bottom: 0,
            width: analyticsExpanded 
              ? '100%' 
              : isMobile 
                ? '100%' 
                : `${analyticsPanelWidth}%`, // Resizable width on desktop
            minWidth: analyticsExpanded || isMobile ? 'auto' : '425px', // Minimum width on desktop
            maxWidth: analyticsExpanded || isMobile ? 'auto' : '70%', // Maximum width on desktop
          zIndex: analyticsPanelOpen ? 1200 : -1, // Hide behind everything when closed
            backgroundColor: 'background.paper',
            boxShadow: analyticsExpanded 
              ? 'none' 
              : '4px 0 16px rgba(0,0,0,0.1)', // Shadow to separate from map
            transition: isResizing ? 'none' : 'left 0.3s ease-in-out, width 0.3s ease-in-out, right 0.3s ease-in-out',
            // When not expanded, add border to look like split
            borderRight: analyticsExpanded || isMobile ? 'none' : '1px solid',
            borderColor: 'divider',
          visibility: analyticsPanelOpen ? 'visible' : 'hidden', // Hide visually but keep mounted
          pointerEvents: analyticsPanelOpen ? 'auto' : 'none', // Disable interactions when hidden
          }}
        >
          <Paper
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: 0,
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              backgroundColor: 'background.paper',
              position: 'relative', // For resize handle positioning
            }}
          >
            {/* Analytics Header */}
            <AnalyticsHeader
              onClose={toggleAnalyticsPanel}
              onFullscreen={toggleAnalyticsExpanded}
              showFullscreen={!isMobile}
              isFullscreen={analyticsExpanded}
            />

            {/* Resize Handle - Only show when not expanded and not mobile */}
            {!analyticsExpanded && !isMobile && (
              <Box
                onMouseDown={handleResizeStart}
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  cursor: 'col-resize',
                  zIndex: 1,
                }}
              />
            )}

            {/* Panel Content */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                backgroundColor: 'background.paper'
              }}
            >
              {analyticsPanel}
            </Box>
          </Paper>
        </Box>

      {/* Confirm when View on Map targets a lease hidden by the flag filter */}
      <Dialog
        open={hiddenLeaseDialogOpen}
        onClose={() => {
          setHiddenLeaseDialogOpen(false);
          pendingNavigateRef.current = null;
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Lease Hidden by Filter</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This lease has no flags matching the current map filter, so it isn&apos;t shown.
            Turn off &quot;Hide without Flags&quot; to view it on the map?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            onClick={() => {
              setHiddenLeaseDialogOpen(false);
              pendingNavigateRef.current = null;
            }}
          >
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={confirmShowAllAndViewLease} autoFocus>
            Show All & View
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm switch from County view when entering a selection mode */}
      <Dialog
        open={switchToLeaseDialogOpen}
        onClose={cancelSwitchToLeaseForSelection}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Switch to Location View</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Map selection and filtering work on leases and wells, not entire counties.
            Continue to switch to Location view and start selecting?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={cancelSwitchToLeaseForSelection}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={confirmSwitchToLeaseForSelection} autoFocus>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MapViewContainer;